import { NumberField } from '@schuettflix/planum-react';
import React from 'react';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';
import { Controller, useFormContext, UseControllerProps } from 'react-hook-form';
import { SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/cn.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';

export type QuantityProps = Pick<UseControllerProps, 'name' | 'defaultValue' | 'disabled'> & {
    testId?: string;
    onQuantityChange?: (quantity?: number) => void;
};

export type UnitProps = Pick<UseControllerProps, 'name' | 'defaultValue'> & {
    variant?: 'lg' | 'md';
    disabled?: boolean;
    supportedUnitNames?: SUPPORTED_UNITS[];
    onUnitChange?: (unit?: SUPPORTED_UNITS) => void;
};

export type QuantityAndUnitInputProps = {
    quantityProps: QuantityProps;
    unitProps: UnitProps;
    className?: string;
};

export const QUANTITY_DEFAULT_NAME = 'quantity';
export const UNIT_DEFAULT_NAME = 'unit';

export const QuantityAndUnitInput: React.FC<QuantityAndUnitInputProps> = React.forwardRef<
    HTMLInputElement,
    QuantityAndUnitInputProps
>(({ quantityProps, unitProps, className }, _ref) => {
    const { control } = useFormContext();
    const { t } = useTranslation();

    return (
        <div className={cn('grid grid-cols-2 items-start gap-2', className)}>
            <Controller
                name={quantityProps.name ?? QUANTITY_DEFAULT_NAME}
                defaultValue={quantityProps.defaultValue}
                control={control}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                    <NumberField
                        {...field}
                        value={value ? Number(value) : undefined}
                        size="lg"
                        onValueChange={value => {
                            onChange(value);
                            quantityProps.onQuantityChange?.(value);
                        }}
                        error={fieldState.error?.message}
                        disabled={quantityProps.disabled}
                        step={0.001}
                        placeholder={t('product.quote.quantity')}
                        aria-label={t('product.quote.quantity')}
                        data-test={quantityProps.testId ?? 'quantity-input'}
                    />
                )}
            />
            <Controller
                control={control}
                name={unitProps.name ?? UNIT_DEFAULT_NAME}
                defaultValue={unitProps.defaultValue ?? null}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value ?? undefined}
                        onValueChange={unit => {
                            unitProps.onUnitChange?.(unit);
                            onChange(unit);
                        }}
                        options={(unitProps.supportedUnitNames ?? []).map(unit => ({
                            value: unit,
                            label: t(SUPPORTED_UNIT_LABEL[unit]),
                        }))}
                        disabled={unitProps.disabled}
                        placeholder={t('product.quote.unit')}
                        error={fieldState.error?.message}
                        size={unitProps.variant ?? 'lg'}
                        dataTest="unit-select-input"
                    />
                )}
            />
        </div>
    );
});
